// Finished issues leave every list the moment they are finished.
//
// "Finished" is resolved / abandoned (utils/issueStatus.js on the server;
// `rejected` is here too because a rejected import row and a rejected claim
// read the same way to the person looking at the list). The server already
// leaves them out of a fetch, so the only gap was the row the user just
// ended: it stayed on screen, with its old status, until a reload.
//
// So every list runs its rows through `pruneFinishedIssues` against the
// redux `issues` slice. Nothing is refetched and nothing is guessed - the
// list simply redraws without the row, in the same frame as the click.
//
//   const { finished, reopened, refreshTick } = useIssueListState();
//   const rows = pruneFinishedIssues(raw, finished, reopened);
//
// `refreshTick` is what a refresh icon bumps: put it in the dependency
// array of whatever loads the list and the icon refetches it in place.
import { useSelector } from "react-redux";

export const FINISHED_STATUSES = ["resolved", "abandoned", "rejected"];

export const isFinishedStatus = (s) =>
  FINISHED_STATUSES.includes(String(s || "").toLowerCase());

const EMPTY = {};

/** The redux slice, with the empty shape when the store predates it. */
export function useIssueListState() {
  return useSelector((s) => s?.issues || EMPTY);
}

/** Just the tick, for a list that only wants the refresh icon to work. */
export function useIssueRefreshTick() {
  return useSelector((s) => s?.issues?.refreshTick || 0);
}

/**
 * Drop the rows this session has finished.
 *
 * @param rows      whatever the list holds
 * @param finished  redux `issues.finished` (id -> terminal status)
 * @param reopened  redux `issues.reopened` (ids put back into play)
 * @param opts.idOf         how to read the issue id off a row (default row._id).
 *                          Review-queue rows are proposals ABOUT an issue, so
 *                          they pass `(r) => r.issueId?._id || r.issueId`.
 * @param opts.byStatus     also drop rows whose own status is finished. Off by
 *                          default: a list the user pointed AT resolved issues
 *                          (Status: Resolved) must still show them.
 * @param opts.keepStatuses  terminal statuses the reader has ASKED to see. The
 *                          issue manager's "Hide resolved" / "Hide abandoned"
 *                          boxes pass whichever is unticked, so unhiding
 *                          resolved issues keeps the one just resolved.
 * @param opts.disabled     leave the list exactly as it came (an explicit
 *                          status filter is on, or this view wants the lot).
 */
export function pruneFinishedIssues(rows, finished, reopened, opts = {}) {
  if (!Array.isArray(rows) || !rows.length) return rows || [];
  if (opts.disabled) return rows;
  const fin = finished || EMPTY;
  const back = reopened || EMPTY;
  const idOf = opts.idOf || ((r) => r && r._id);
  const statusOf = opts.statusOf || ((r) => r && r.status);
  const keep = opts.keepStatuses || [];
  const anyFinished = Object.keys(fin).length > 0;
  if (!anyFinished && !opts.byStatus) return rows;
  const out = rows.filter((r) => {
    const id = String(idOf(r) || "");
    if (id && back[id]) return true;               // reopened wins over a stale status
    if (id && fin[id]) return keep.includes(fin[id]);  // finished in this session
    if (opts.byStatus && isFinishedStatus(statusOf(r))) return keep.includes(String(statusOf(r)).toLowerCase());
    return true;
  });
  return out.length === rows.length ? rows : out;   // same array when nothing went
}

export default pruneFinishedIssues;
